import { useMemo } from "react";
import {
  MatrixTable,
  SkeletonBase,
  type MatrixTableColumn,
  type MatrixTableGroupColumn,
} from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import type { AnnualScheduleGroup } from "../../adapters";
import type { AnnualScheduleWeek } from "../../types";
import { annualScheduleColumnWidths } from "../../lib";
import { useAnnualScheduleLayout } from "../../hooks/use-annual-schedule-layout";
import styles from "./annualScheduleSkeleton.module.scss";

interface PlaceholderRow {
  id: string;
  index: number;
}

interface PlaceholderGroup {
  id: string;
  rows: PlaceholderRow[];
}

interface Props {
  groups: AnnualScheduleGroup[];
  weeks: AnnualScheduleWeek[];
}

const initialWeeks = Array.from({ length: 12 }, (_, index) => ({
  id: "placeholder-week-" + index,
  name: "S" + (index + 1),
}));
const initialGroups: PlaceholderGroup[] = Array.from({ length: 6 }, (_, index) => ({
  id: "placeholder-league-" + index,
  rows: [0, 1].map(row => ({ id: String(row), index: row })),
}));

/**
 * Mirrors the real grouped matrix while its main query is pending.
 *
 * @remarks Reuses column sizing, pinning, borders and row heights from MatrixTable.
 * During refreshes, placeholders preserve the known league rows and week axis.
 * Initial loads show six two-component leagues without inventing visible week labels.
 */
export default function AnnualScheduleSkeleton({ groups, weeks }: Props) {
  const { t } = useTranslation();
  const visibleWeeks = weeks.length ? weeks : initialWeeks;
  const { containerRef, weekWidths } = useAnnualScheduleLayout(visibleWeeks);
  const placeholderGroups = useMemo(
    () =>
      groups.length
        ? groups.map(group => ({
            id: group.id,
            rows: group.rows.map((row, index) => ({ id: row.id, index })),
          }))
        : initialGroups,
    [groups],
  );
  const groupColumns: MatrixTableGroupColumn<PlaceholderGroup>[] = [
    {
      key: "subject",
      header: t("dimension.package.annual.league"),
      width: annualScheduleColumnWidths.subject,
      pinned: "start",
      renderGroupCell: () => (
        <div className={styles.annualScheduleSkeleton_identity}>
          <SkeletonBase width={148} height={18} radius={4} />
          <SkeletonBase width={164} height={14} radius={4} />
        </div>
      ),
    },
    {
      key: "teacher",
      header: t("dimension.package.annual.teacher"),
      width: annualScheduleColumnWidths.teacher,
      pinned: "start",
      renderGroupCell: () => (
        <div className={styles.annualScheduleSkeleton_identity}>
          <SkeletonBase width={148} height={18} radius={4} />
          <SkeletonBase width={28} height={16} radius={8} />
        </div>
      ),
    },
  ];
  const columns: MatrixTableColumn<PlaceholderGroup, PlaceholderRow>[] = [
    {
      key: "component",
      header: t("dimension.package.annual.component"),
      width: annualScheduleColumnWidths.component,
      pinned: "start",
      isRowHeader: true,
      renderCell: () => (
        <div className={styles.annualScheduleSkeleton_placeholder}>
          <SkeletonBase width={64} height={16} radius={8} />
        </div>
      ),
    },
    ...visibleWeeks.map((week, index) => ({
      key: week.id,
      width: weekWidths[index],
      header: weeks.length ? (
        week.name
      ) : (
        <div className={styles.annualScheduleSkeleton_placeholder}>
          <SkeletonBase width={20} height={14} radius={4} />
        </div>
      ),
      renderCell: (row: PlaceholderRow) =>
        (index + row.index) % 3 !== 2 ? (
          <div className={styles.annualScheduleSkeleton_placeholder}>
            <SkeletonBase width={28} height={28} radius={4} />
          </div>
        ) : null,
    })),
  ];

  return (
    <div
      ref={containerRef}
      className={styles.annualScheduleSkeleton}
      role="status"
      aria-label={t("dimension.package.annual.loading")}
      aria-busy="true"
    >
      <div
        aria-hidden="true"
        ref={element => {
          element?.setAttribute("inert", "");
        }}
      >
        <MatrixTable
          groups={placeholderGroups}
          groupColumns={groupColumns}
          columns={columns}
          getGroupKey={group => group.id}
          getRows={group => group.rows}
          getRowKey={row => row.id}
          accessibleLabel={t("dimension.package.annual.loading")}
          isBusy
        />
        <div className={styles.annualScheduleSkeleton_pagination}>
          <SkeletonBase width={160} height={36} radius={4} />
          <SkeletonBase width={180} height={36} radius={4} />
        </div>
      </div>
    </div>
  );
}
